import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submit } from 'redux-form';
import { countWithoutPack, createPack, deletePack, editPack, getPacks, PACK_COVER_URL, reorderPacks, updatePack } from '../../actions/packs';
import { push } from '../../actions/utils';
import AddIconButton from '../../components/buttons/AddIconButton';
import CustomCard from '../../components/CustomCard';
import ModalDialog from '../../components/ModalDialog';
import OrderableDataGrid from '../../components/OrderableDataGrid';
import { NO_PACK, packPath, withNoPackRow } from '../../packNav';
import { validatePack } from '../../validation';
import { PackCreationForm, PackEditionForm } from './PackForm';

const describe = (pack) => {
  const status = pack.virtual ? 'Éléments sans pack' : [pack.published ? 'Publié' : 'Brouillon', pack.isBase ? 'Pack de base' : null].filter(Boolean).join(' · ');
  const { templates = 0, backgrounds = 0, overlays = 0, decorations = 0 } = pack.counts || {};
  return `${status} — ${templates} templates, ${backgrounds} fonds, ${overlays} emojis, ${decorations} décorations`;
};

const Packs = () => {
  const dispatch = useDispatch();
  const packs = useSelector((state) => state.packs.packs);
  const pack = useSelector((state) => state.packs.pack);
  const [dialog, setDialog] = useState(null); // 'create' | 'edit' | null
  const [noPackCounts, setNoPackCounts] = useState(null);

  useEffect(() => {
    dispatch(getPacks());
  }, [dispatch]);

  // re-counted whenever the packs reload (an item may have been moved into a pack)
  useEffect(() => {
    countWithoutPack()
      .then(setNoPackCounts)
      .catch((error) => console.error(error));
  }, [packs]);

  const openPack = (id) => push(packPath(id));

  const close = () => setDialog(null);

  const handleCreate = async (values) => {
    validatePack(values);
    await dispatch(createPack(values));
    close();
  };

  const handleUpdate = async (values) => {
    validatePack(values);
    await dispatch(updatePack(pack, values));
    close();
  };

  const handleEdit = async (id) => {
    await dispatch(editPack(id));
    setDialog('edit');
  };

  const handleMove = (id, up) => {
    if (id === NO_PACK) return;
    const sorted = [...packs];
    const index = sorted.findIndex((p) => p.objectId === id);
    const target = up ? index - 1 : index + 1;
    if (target < 0 || target >= sorted.length) return;
    [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
    dispatch(reorderPacks(sorted));
  };

  const formatData = useCallback(() => {
    const rows = withNoPackRow(packs, noPackCounts ?? {});
    return {
      data: rows.map((p) => ({
        objectId: p.objectId,
        virtual: !!p.virtual,
        image: p.cover ? PACK_COVER_URL + p.cover : null,
        name: p.name,
        icon: p.icon,
        description: describe(p),
      })),
      totalCount: rows.length,
    };
  }, [packs, noPackCounts]);

  return (
    <>
      <CustomCard
        title="Packs"
        actionHeaderButtons={<AddIconButton onAdd={() => setDialog('create')} />}
        content={
          <OrderableDataGrid
            onFormatData={formatData}
            onEdit={handleEdit}
            onDelete={(id) => dispatch(deletePack(id))}
            onChange={() => {}}
            onMove={handleMove}
            onPreview={openPack}
            onRowClick={openPack}
            FilterText=""
          />
        }
        withActionButtons={false}
        fullScreen
      />
      <ModalDialog
        title="Nouveau pack"
        content={<PackCreationForm onSubmit={handleCreate} />}
        isVisible={dialog === 'create'}
        onConfirm={() => dispatch(submit('packForm'))}
        onClose={close}
      />
      <ModalDialog
        title={`Modifier ${pack?.name ?? ''}`}
        content={<PackEditionForm onSubmit={handleUpdate} />}
        isVisible={dialog === 'edit'}
        onConfirm={() => dispatch(submit('packForm'))}
        onClose={close}
        labelConfirm="Enregistrer"
      />
    </>
  );
};

export default Packs;
